// Reset password pubblico: l'utente imposta la nuova password dal link monouso
// generato dal SuperAdmin (RESET ACCESS). Token hash a riposo, scadenza 60 min.
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Wordmark } from "@/components/landing/shared";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";

export default function ResetPassword() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get(`/auth/reset-password/${token}`)
      .then(({ data }) => setInfo(data))
      .catch((e) => setError(formatApiError(e)));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      setError("Le password non coincidono");
      return;
    }
    setError(null);
    setLoading(true);
    try {
      await api.post(`/auth/reset-password/${token}`, { password });
      setDone(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6"
      data-testid="reset-password-page">
      <Card className="w-full max-w-md border-slate-200 shadow-sm">
        <CardContent className="p-8">
          <div className="mb-6">
            <Wordmark />
          </div>

          {done ? (
            <div className="space-y-4" data-testid="reset-success">
              <h1 className="text-xl font-bold text-slate-900">Password aggiornata</h1>
              <p className="text-sm text-slate-600">
                Le sessioni precedenti sono state chiuse. Accedi con la nuova password.
              </p>
              <Link to="/login">
                <Button className="w-full bg-green-700 hover:bg-green-800"
                  data-testid="reset-goto-login">Vai al login</Button>
              </Link>
            </div>
          ) : error && !info ? (
            <div className="space-y-4" data-testid="reset-invalid">
              <h1 className="text-xl font-bold text-slate-900">Link non valido</h1>
              <p className="text-sm text-red-700">{error}</p>
              <p className="text-xs text-slate-500">
                I link di reset scadono dopo 60 minuti e sono monouso: chiedi un nuovo
                reset all'amministratore della piattaforma.</p>
            </div>
          ) : !info ? (
            <p className="text-sm text-slate-600" data-testid="reset-loading">
              <Loader2 className="w-4 h-4 animate-spin inline mr-2" aria-hidden="true" />
              Verifica link…</p>
          ) : (
            <div className="space-y-4">
              <div>
                <h1 className="text-xl font-bold text-slate-900">Imposta nuova password</h1>
                <p className="text-sm text-slate-600 mt-1" data-testid="reset-summary">
                  Account: {info.email} · il link scade {info.expires_at?.slice(0, 16).replace("T", " ")}
                </p>
              </div>
              {error && (
                <div role="alert" data-testid="reset-error"
                  className="p-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-800">
                  {error}</div>
              )}
              <form onSubmit={submit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="reset-password">Nuova password</Label>
                  <Input id="reset-password" type="password" required minLength={8}
                    autoComplete="new-password" data-testid="reset-password-input"
                    value={password} onChange={(e) => setPassword(e.target.value)} />
                  <p className="text-[11px] text-slate-500">Minimo 8 caratteri.</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reset-confirm">Conferma password</Label>
                  <Input id="reset-confirm" type="password" required minLength={8}
                    autoComplete="new-password" data-testid="reset-confirm-input"
                    value={confirm} onChange={(e) => setConfirm(e.target.value)} />
                </div>
                <Button type="submit" disabled={loading || password.length < 8}
                  className="w-full bg-green-700 hover:bg-green-800"
                  data-testid="reset-submit">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : "Aggiorna password"}
                </Button>
              </form>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
